"use client";

import React from "react";
import { Clock, MonitorPlay, Award, CheckCircle2, Sparkles, Thermometer, Zap, Wind, Wrench, ArrowRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const syllabusData = [{
  id: "modulo-1",
  title: "Fundamentos de Refrigeración",
  description: "Conceptos clave de termodinámica y el ciclo de refrigeración esencial para entender el enfriamiento.",
  icon: Thermometer,
  lessons: ["Principios de termodinámica", "El ciclo de refrigeración por compresión", "Tipos de gases refrigerantes y normativa", "Relación presión-temperatura"]
}, {
  id: "modulo-2",
  title: "Electricidad y Controles",
  description: "Estudio de circuitos eléctricos, motores y sistemas de control específicos para equipos de AC.",
  icon: Zap,
  lessons: ["Circuitos eléctricos de potencia y mando", "Motores, compresores y capacitores", "Placas electrónicas y sensores", "Protecciones y térmicos"]
}, {
  id: "modulo-3",
  title: "Instalación Profesional",
  description: "Técnicas de montaje, uso de herramientas específicas y protocolos de puesta en marcha.",
  icon: Wind,
  lessons: ["Herramientas del técnico frigorista", "Montaje de unidades Split y Multi-split", "Pestañado, soldadura y vacío", "Carga de refrigerante y balance térmico"]
}, {
  id: "modulo-4",
  title: "Mantenimiento y Fallas",
  description: "Detección de fallas comunes, protocolos de limpieza y seguridad en el trabajo técnico.",
  icon: Wrench,
  lessons: ["Mantenimiento preventivo y limpieza", "Detección y reparación de fugas", "Diagnóstico de fallas mecánicas", "Normas de seguridad y EPP"]
}];
const courseDetails = [{
  id: "duration",
  label: "Duración",
  value: "12 Semanas",
  icon: Clock,
  isMono: true
}, {
  id: "modality",
  label: "Modalidad",
  value: "100% Presencial (En vivo)",
  icon: MonitorPlay,
  isMono: false
}, {
  id: "level",
  label: "Nivel",
  value: "Intermedio / Avanzado",
  icon: Award,
  isMono: false
}];
export default function DetallesDelCurso() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: sidebarRef,
    isVisible: isSidebarVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="detalles-del-curso" className="bg-background relative py-20 md:py-32 overflow-hidden" ref={sectionRef}>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/5 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className={`lg:col-span-8 transition-all duration-1000 transform ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                Plan de Estudios
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Un recorrido técnico estructurado para llevar tus habilidades al siguiente nivel, 
                combinando teoría profunda con práctica intensiva.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {syllabusData.map((module, i) => <div data-index={i} key={module.id}>
                  <AccordionItem value={module.id} className="group border border-border/50 rounded-2xl bg-background/70 backdrop-blur-[12px] px-2 sm:px-6 transition-all duration-300 hover:scale-[1.02] hover:border-secondary/40 hover:shadow-[0_0_20px_rgba(var(--color-secondary),0.15)] data-[state=open]:border-secondary/50 data-[state=open]:shadow-[0_0_20px_rgba(var(--color-secondary),0.1)] overflow-hidden">
                    <AccordionTrigger className="hover:no-underline py-6">
                      <div className="flex items-center gap-4 text-left">
                        <div className="relative flex items-center justify-center size-12 rounded-xl bg-primary/10 text-primary shrink-0 group-hover:bg-primary/20 transition-colors">
                          <module.icon className="size-6 relative z-10" />
                          <Sparkles className="absolute -top-1 -right-1 size-4 text-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-[0_0_8px_var(--color-secondary)]" />
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                            {module.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1 font-normal hidden sm:block">
                            {module.description}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pt-2">
                      <p className="text-sm text-muted-foreground mb-6 sm:hidden">
                        {module.description}
                      </p>
                      <ul className="space-y-3 pl-[4.5rem]">
                        {module.lessons.map((lesson, j) => <li data-index={j} key={j} className="flex items-start gap-3 text-foreground/80">
                            <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                            <span className="text-base">{lesson}</span>
                          </li>)}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>

          <div ref={sidebarRef} className={`lg:col-span-4 lg:sticky lg:top-24 transition-all duration-1000 delay-200 transform ${isSidebarVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <Card className="border-border/50 shadow-xl shadow-shadow-color/5 bg-card/95 backdrop-blur-sm overflow-hidden">
              <div className="h-2 w-full bg-gradient-to-r from-primary to-secondary" />
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold">Ficha del Curso</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-4">
                  {courseDetails.map((detail, i) => <li data-index={i} key={detail.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <div className="flex items-center gap-3 text-muted-foreground">
                        <detail.icon className="size-5" />
                        <span className="font-medium">{detail.label}</span>
                      </div>
                      <span className={`text-foreground font-medium ${detail.isMono ? 'font-mono tracking-tight' : ''}`}>
                        {detail.value}
                      </span>
                    </li>)}
                </ul>

                <div className="pt-6 border-t border-border/50">
                  <p className="text-sm text-muted-foreground mb-2">Inversión única</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-secondary drop-shadow-sm">$19.500</span>
                    <span className="text-lg text-muted-foreground font-medium">$U</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-4 pb-8">
                <Link to="https://mpago.la/1WfMEZk" newTab className="w-full">
                  <Button size="lg" className="w-full text-base font-semibold h-12 bg-[#009EE3] hover:bg-[#008ED2] text-white border-none shadow-md">
                    Pagar con Mercado Pago
                    <ArrowRight className="ml-2 size-4" />
                  </Button>
                </Link>
                <Link to="https://wa.me/59896344395" newTab className="w-full">
                  <Button size="lg" className="w-full text-base font-semibold h-12 bg-[#25D366] hover:bg-[#22c35e] text-white border-none shadow-md">
                    <MessageCircle className="mr-2 size-5" />
                    Consultar por WhatsApp
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}